import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Card,
  Chip,
  Divider,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  Dialog,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import Label from '../../../components/Label';
import { fDateTime } from '../../../utils/formatTime';
import MetricCard from './MetricCard';
import { EmptyModuleState, LoadingPanel, SectionError } from './ManageUserDialogStates';
import { fetchAdmin } from '../../../admin/api';

function formatText(value) {
  if (value === undefined || value === null || value === '') {
    return 'N/A';
  }
  return `${value}`;
}

function formatDateTime(value) {
  if (!value) return 'N/A';
  try {
    return fDateTime(value);
  } catch {
    return formatText(value);
  }
}

function AccountBadge({ bankName, bankId }) {
  const initials = (bankName || bankId || 'BN').slice(0, 2).toUpperCase();

  return (
    <Stack direction="row" spacing={1.5} alignItems="center">
      <Avatar sx={{ width: 34, height: 34, fontSize: 13 }}>
        {initials}
      </Avatar>
      <Box>
        <Typography variant="subtitle2" sx={{ lineHeight: 1.2 }}>
          {formatText(bankName)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Bank ID: {formatText(bankId)}
        </Typography>
      </Box>
    </Stack>
  );
}

AccountBadge.propTypes = {
  bankName: PropTypes.string,
  bankId: PropTypes.string,
};

function DetailField({ label, value, monospace = false }) {
  return (
    <Stack spacing={0.4}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontFamily: monospace ? 'monospace' : 'inherit',
          wordBreak: 'break-word',
        }}
      >
        {value}
      </Typography>
    </Stack>
  );
}

DetailField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  monospace: PropTypes.bool,
};

function BeneficiaryDetailsDialog({ open, onClose, row }) {
  const statusColor = row?.status === 'active' ? 'success' : row?.status === 'resolved' ? 'default' : 'warning';

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ pr: 6 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
          <Box>
            <Typography variant="h6">Beneficiary Audit Details</Typography>
            <Typography variant="body2" color="text.secondary">
              Review the account context used in policy and risk decisions.
            </Typography>
          </Box>
          <IconButton onClick={onClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 3 }}>
        {row ? (
          <Stack spacing={3}>
            <Card variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                <AccountBadge bankName={row.bank_name} bankId={row.bank_id} />
                <Label color={statusColor}>{formatText(row.status)}</Label>
              </Stack>
            </Card>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ p: 2.5, borderRadius: 2, height: '100%' }}>
                  <Typography variant="subtitle2" sx={{ mb: 2 }}>
                    Account Information
                  </Typography>
                  <Stack spacing={2}>
                    <DetailField label="Account name" value={formatText(row.account_name)} />
                    <DetailField label="Account number" value={formatText(row.account_number)} monospace />
                    <DetailField label="Channel" value={formatText(row.channel)} />
                    <DetailField label="User ID" value={formatText(row.user_id)} monospace />
                  </Stack>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ p: 2.5, borderRadius: 2, height: '100%' }}>
                  <Typography variant="subtitle2" sx={{ mb: 2 }}>
                    Risk / Review Signals
                  </Typography>
                  <Stack spacing={2}>
                    <DetailField label="Attempts" value={formatText(row.attempt_count)} />
                    <DetailField label="Last attempted" value={formatDateTime(row.last_attempted_at)} />
                    <DetailField label="Last reference" value={formatText(row.last_reference)} monospace />
                    <DetailField label="Status" value={formatText(row.status)} />
                  </Stack>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 2 }}>
                    Record Timeline
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <DetailField label="Created at" value={formatDateTime(row.created_at)} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <DetailField label="Updated at" value={formatDateTime(row.updated_at)} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <DetailField label="Row ID" value={formatText(row.id)} monospace />
                    </Grid>
                  </Grid>
                </Card>
              </Grid>
            </Grid>
          </Stack>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

BeneficiaryDetailsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  row: PropTypes.object,
};

export default function BeneficiariesTab({ userId }) {
  const [state, setState] = useState({
    loading: false,
    loaded: false,
    error: null,
    rows: [],
  });
  const [refreshKey, setRefreshKey] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [selectedRow, setSelectedRow] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const loadBeneficiaries = useCallback(async () => {
    if (!userId) return;

    setState((prev) => ({
      ...prev,
      loading: true,
      error: null,
    }));

    try {
      const response = await fetchAdmin(`/users/${userId}/beneficiaries`);
      const rows = Array.isArray(response) ? response : response?.data || [];

      setState({
        loading: false,
        loaded: true,
        error: null,
        rows,
      });

      setSelectedRow((current) => current && rows.some((r) => String(r.id) === String(current.id)) ? current : rows[0] || null);
    } catch (error) {
      setState((prev) => ({
        ...prev,
        loading: false,
        loaded: true,
        error: error?.message || 'Failed to load beneficiaries.',
      }));
    }
  }, [userId]);

  useEffect(() => {
    loadBeneficiaries();
  }, [loadBeneficiaries, refreshKey]);

  const rows = useMemo(() => state.rows || [], [state.rows]);

  const pagedRows = useMemo(() => {
    const start = page * pageSize;
    return rows.slice(start, start + pageSize);
  }, [rows, page, pageSize]);

  const total = rows.length;
  const reviewCount = useMemo(() => rows.filter((row) => row.status === 'active' || row.requires_review).length, [rows]);
  const bankCount = useMemo(() => new Set(rows.map((row) => row.bank_id).filter(Boolean)).size, [rows]);

  if (!userId) {
    return (
      <EmptyModuleState
        title="No user selected"
        description="Load a user profile to inspect beneficiary observations."
        icon="solar:user-id-bold"
      />
    );
  }

  if (state.loading && !state.loaded) {
    return <LoadingPanel />;
  }

  if (state.error) {
    return (
      <SectionError
        message={state.error}
        onRetry={() => {
          setRefreshKey((value) => value + 1);
        }}
      />
    );
  }

  return (
    <Stack spacing={3}>
      <Grid container spacing={2.5}>
        <Grid item xs={12} md={4}>
          <MetricCard
            title="Beneficiaries"
            value={total}
            caption="Observed beneficiary accounts returned for this user."
            icon="solar:wallet-bold"
            color="primary"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <MetricCard
            title="Active Observations"
            value={reviewCount}
            caption="Records currently marked active or requiring attention."
            icon="solar:danger-triangle-bold"
            color="warning"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <MetricCard
            title="Unique Banks"
            value={bankCount}
            caption="Distinct beneficiary banks observed in the response."
            icon="solar:buildings-bold"
            color="success"
          />
        </Grid>
      </Grid>

      <Card variant="outlined" sx={{ borderRadius: 2.5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 3, py: 2 }}>
          <Box>
            <Typography variant="h6">Beneficiary Accounts</Typography>
            <Typography variant="body2" color="text.secondary">
              User bank beneficiary accounts, status, and review signals.
            </Typography>
          </Box>
          <Chip label={`${total} records`} variant="outlined" />
        </Stack>

        <Divider />

        {rows.length ? (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Bank</TableCell>
                    <TableCell>Account</TableCell>
                    <TableCell>Channel</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Attempts</TableCell>
                    <TableCell>Last attempted</TableCell>
                    <TableCell>Updated</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedRows.map((row) => {
                    const isSelected = String(selectedRow?.id) === String(row.id);

                    return (
                      <TableRow
                        key={row.id}
                        hover
                        selected={isSelected}
                        onClick={() => {
                          setSelectedRow(row);
                          setDetailsOpen(true);
                        }}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>
                          <Stack spacing={0.3}>
                            <Typography variant="subtitle2">{formatText(row.bank_name)}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatText(row.bank_id)}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Stack spacing={0.3}>
                            <Typography variant="subtitle2" sx={{ fontFamily: 'monospace' }}>
                              {formatText(row.account_number)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatText(row.account_name)}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>{formatText(row.channel)}</TableCell>
                        <TableCell>
                          <Label color={row.status === 'active' ? 'success' : 'default'}>
                            {formatText(row.status)}
                          </Label>
                        </TableCell>
                        <TableCell align="right">{formatText(row.attempt_count)}</TableCell>
                        <TableCell>{formatDateTime(row.last_attempted_at)}</TableCell>
                        <TableCell>{formatDateTime(row.updated_at)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <Divider />

            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, nextPage) => setPage(nextPage)}
              rowsPerPage={pageSize}
              onRowsPerPageChange={(event) => {
                setPage(0);
                setPageSize(parseInt(event.target.value, 10));
              }}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          </>
        ) : (
          <Box sx={{ p: 3 }}>
            <EmptyModuleState
              title="No beneficiaries"
              description="The beneficiaries endpoint returned no beneficiary observations."
              icon="solar:wallet-bold"
            />
          </Box>
        )}
      </Card>

      <BeneficiaryDetailsDialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        row={selectedRow}
      />
    </Stack>
  );
}

BeneficiariesTab.propTypes = {
  userId: PropTypes.string,
};